import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Grid, Drawer, Button, message, Typography } from 'antd';
import { AppstoreOutlined, UserOutlined, BookOutlined, MenuOutlined, LogoutOutlined, UserAddOutlined, PhoneOutlined, CalendarOutlined, DashboardOutlined, FolderOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import { Avatar, Tag } from 'antd';
import vastuvitLogo from '../assets/vastuvit_logo.png';
import NotificationBell from '../pages/calls/NotificationBell';
import GlobalSearch from '../components/GlobalSearch';
import ErrorBoundary from '../components/ErrorBoundary';
import { hasPerm, getUser, clearSession } from '../auth/permissions';
import './DashboardLayout.css';

const { Header, Content, Footer, Sider } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

const DashboardLayout = () => {
  const screens = useBreakpoint();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Submenu collapsed by default; auto-open only when inside a Call page.
  const [openKeys, setOpenKeys] = useState(
    location.pathname.startsWith('/calls') ? ['call-followup'] : []
  );
  useEffect(() => {
    if (location.pathname.startsWith('/calls')) {
      setOpenKeys((k) => (k.includes('call-followup') ? k : [...k, 'call-followup']));
    }
  }, [location.pathname]);

  // Determine selected menu key based on current path
  let selectedKey = 'quotations';
  if (location.pathname.startsWith('/dashboard')) selectedKey = 'dashboard';
  else if (location.pathname.startsWith('/documents')) selectedKey = 'documents';
  else if (location.pathname.startsWith('/users')) selectedKey = 'users';
  else if (location.pathname.startsWith('/settings')) selectedKey = 'settings';
  else if (location.pathname.startsWith('/calendar')) selectedKey = 'calendar';
  else if (location.pathname.includes('/clients')) selectedKey = 'clients';
  else if (location.pathname.startsWith('/calls')) {
    const seg = location.pathname.split('/')[2];
    selectedKey = seg ? `calls/${seg}` : 'calls';
  }
  else if (location.pathname.includes('/leads')) selectedKey = 'leads';
  else if (
    location.pathname.includes('/packages') ||
    location.pathname.includes('/components')
  ) selectedKey = 'packages';
  else if (
    location.pathname.includes('/quotations') ||
    location.pathname.includes('/quotation-details') ||
    location.pathname.includes('/quotation-view')
  ) selectedKey = 'quotations';

  const handleLogout = () => {
    clearSession();
    message.success('Logged out successfully');
    navigate('/login');
  };

  const currentUser = getUser();

  const rawItems = [
    {
      key: 'dashboard', perm: 'view_dashboard',
      icon: <DashboardOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/dashboard" style={{ fontSize: '14px', fontWeight: 500 }}>Dashboard</Link>,
    },
    {
      key: 'leads', perm: 'view_leads',
      icon: <UserAddOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/leads" style={{ fontSize: '14px', fontWeight: 500 }}>Lead Management</Link>,
    },
    {
      key: 'clients', perm: 'view_clients',
      icon: <UserOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/clients" style={{ fontSize: '14px', fontWeight: 500 }}>Clients</Link>,
    },
    {
      key: 'quotations', perm: 'view_quotations',
      icon: <AppstoreOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/quotations" style={{ fontSize: '14px', fontWeight: 500 }}>Quotations</Link>,
    },
    {
      key: 'packages', perm: 'view_packages',
      icon: <BookOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/packages" style={{ fontSize: '14px', fontWeight: 500 }}>Packages</Link>,
    },
    { type: 'divider' },
    {
      key: 'call-followup', perm: 'view_calls',
      icon: <PhoneOutlined style={{ fontSize: '18px' }} />,
      label: <span style={{ fontSize: '14px', fontWeight: 500 }}>Call &amp; Follow-up</span>,
      children: [
        { key: 'calls', label: <Link to="/calls">Dashboard</Link> },
        { key: 'calls/new', label: <Link to="/calls/new">New Follow-up</Link> },
        { key: 'calls/today', label: <Link to="/calls/today">Today's Follow-ups</Link> },
        { key: 'calls/upcoming', label: <Link to="/calls/upcoming">Upcoming</Link> },
        { key: 'calls/overdue', label: <Link to="/calls/overdue">Overdue</Link> },
        { key: 'calls/completed', label: <Link to="/calls/completed">Completed</Link> },
        { key: 'calls/missed', label: <Link to="/calls/missed">Missed</Link> },
        { key: 'calls/history', label: <Link to="/calls/history">Call History</Link> },
        { key: 'calls/reports', label: <Link to="/calls/reports">Reports</Link> },
      ],
    },
    {
      key: 'calendar', perm: 'view_calendar',
      icon: <CalendarOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/calendar" style={{ fontSize: '14px', fontWeight: 500 }}>Calendar</Link>,
    },
    { type: 'divider' },
    {
      key: 'documents', perm: 'view_documents',
      icon: <FolderOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/documents" style={{ fontSize: '14px', fontWeight: 500 }}>Document Management</Link>,
    },
    {
      key: 'users', perm: 'manage_users',
      icon: <TeamOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/users" style={{ fontSize: '14px', fontWeight: 500 }}>Users &amp; Roles</Link>,
    },
    {
      key: 'settings', perm: 'manage_settings',
      icon: <SettingOutlined style={{ fontSize: '18px' }} />,
      label: <Link to="/settings" style={{ fontSize: '14px', fontWeight: 500 }}>Settings</Link>,
    },
  ];

  // Hide items the current role can't access (dividers kept only between visible groups)
  const visible = rawItems.filter(it => it.type === 'divider' || !it.perm || hasPerm(it.perm));
  const menuItems = visible.filter((it, i) =>
    !(it.type === 'divider' && (i === 0 || i === visible.length - 1 || visible[i - 1]?.type === 'divider'))
  );

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="logo" style={{  
        textAlign: 'center',
        borderBottom: '1px solid #f0f0f0',
        background: '#ffffff'
      }}>
        <img 
          src={vastuvitLogo} 
          alt="Vastuvit Logo" 
          style={{ 
            maxWidth: '140px', 
            height: '100px',
            transition: 'all 0.3s ease'
          }}
        />
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={menuItems}
        style={{
          borderRight: 'none',
          padding: '8px 6px',
          background: '#ffffff',
          flex: 1,
          minHeight: 0
        }}
        className="custom-menu"
      />
      <Button
        type="text"
        icon={<LogoutOutlined style={{ fontSize: '18px' }} />}
        onClick={handleLogout}
        style={{
          width: '100%',
          justifyContent: 'flex-start',
          borderTop: '1px solid #f0f0f0',
          margin: '8px 0',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#d4380d',
          background: '#fff',
        }}
      >
        Logout
      </Button>
    </div>
  );
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {screens.md ? (
        <Sider
          width={230}
          theme="light"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'sticky',
            top: 0,
            left: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 1000,
            background: '#ffffff'
          }}
        >
          {sidebarContent}
        </Sider>
      ) : (
        <Drawer
          placement="left"
          closable={true}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={240}
          bodyStyle={{ padding: 0 }}
          style={{
            position: 'absolute'
          }}
        >
          {sidebarContent}
        </Drawer>
      )}
      <Layout>
        <Header style={{ 
          background: '#ffffff', 
          padding: '0 24px', 
          display: 'flex', 
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 999,
          height: '64px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            {!screens.md && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: '18px' }} />}
                onClick={() => setDrawerVisible(true)}
                style={{ padding: '4px 8px' }}
              />
            )}
            <Text strong style={{ fontSize: '16px', margin: 0 }}>Vastuvit</Text>
            {screens.md && <GlobalSearch style={{ width: 280, marginLeft: 8 }} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <NotificationBell />
            {currentUser && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size="small" style={{ backgroundColor: '#e27e56' }}>
                  {(currentUser.Name || currentUser.Username || '?').charAt(0).toUpperCase()}
                </Avatar>
                {screens.sm && (
                  <div style={{ lineHeight: 1.1 }}>
                    <Text strong style={{ fontSize: 13 }}>{currentUser.Name || currentUser.Username}</Text>
                    {currentUser.Role && <div><Tag color="orange" style={{ fontSize: 10, lineHeight: '14px', margin: 0 }}>{currentUser.Role}</Tag></div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </Header>

        <Content style={{ 
          margin: '8px', 
          padding: '8px', 
          background: '#ffffff', 
          borderRadius: 8,
          minHeight: 'calc(100vh - 180px)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
        }}>
          <ErrorBoundary routeKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Content>

        <Footer style={{ 
          textAlign: 'center', 
          background: '#ffffff', 
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          fontSize: '14px',
          color: 'rgba(0, 0, 0, 0.65)'
        }}>
          Developed By endsecure. © {new Date().getFullYear()} Vastuvit. All rights reserved.
        </Footer>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
